/**
 * @module engine3d/flow
 * @summary How a moving stream is drawn: its colour, its rhythm, its speed.
 * @description
 *   "Run the Flow" sets the plant going: slugs of material and streaks of air
 *   travelling along the pipework, each in the direction its run was drawn.
 *   WHAT a run carries is a fact about the drawing and is decided in
 *   `engine/streams`; this module is only concerned with how the answer looks
 *   once it is moving.
 *
 *   Colour alone is a weak signal — it does not survive a monochrome print, and
 *   a fair number of engineers cannot rely on it at all — so the two streams
 *   differ in RHYTHM as well. Material moves as long, chunky slugs with a soft
 *   trailing edge; air moves as short, quick streaks at nearly three times the
 *   speed. Which is which reads from across the room, and from a still frame.
 */
import type { FlowMedium } from '../types';

// ─── How a stream is drawn ───────────────────────────────────────────────

/** One stream's look. Distances are world units; speed is world units/second. */
export interface FlowStyle {
  /**
   * Slug colour, mixed into the pipe's own albedo. Deliberately hotter than
   * white (>1) so a slug reads as lit from inside the pipe rather than as a
   * stripe painted on the outside of it.
   */
  col: [number, number, number];
  /** Distance from the head of one slug to the head of the next. */
  wave: number;
  /** Leading edge, as a fraction of the wavelength. */
  head: number;
  /** Trailing edge, as a fraction of the wavelength. */
  tail: number;
  /** World units a slug travels per second of animation. */
  speed: number;
  /** How hard a slug paints over the pipe at its peak, 0..1. */
  strength: number;
}

/**
 * The two streams.
 *
 * Material: amber, long slugs, unhurried, with a tail nearly three times its
 * head — that asymmetry is what makes a slug read as being dragged along
 * rather than as a mark sliding past. Air: cyan, shorter, and three times the
 * speed, which the eye reads as a gas without being told.
 *
 * The wavelengths are set at PLANT scale, not pipe scale: about four metres for
 * material at the default 25 mm per unit. Anything much finer turns into
 * speckle the moment you zoom out far enough to see the whole line, which is
 * the view this exists for.
 */
export const FLOW_STYLES: Record<'material' | 'air', FlowStyle> = {
  material: { col: [1.42, 0.86, 0.22], wave: 160, head: 0.12, tail: 0.3, speed: 165, strength: 0.95 },
  air: { col: [0.4, 1.12, 1.55], wave: 104, head: 0.15, tail: 0.22, speed: 470, strength: 0.86 },
};

/**
 * How many sections of pipe wall one slug is built from.
 *
 * A shaded triangle is a single flat colour, so this is what decides whether a
 * slug reads as a soft travelling band or as a row of blocks — and it is paid
 * for in triangles on every run in the plant. Six is where the leading edge
 * stops looking stepped; below four it plainly does.
 */
const SECTIONS_PER_SLUG = 6;

/**
 * Longest section of pipe wall on a run carrying `m`, in world units, or 0 for
 * a run that is not animated and should be swept the ordinary way.
 *
 * Derived from the stream rather than fixed, because the two streams are not
 * the same size: an air streak is little over half the length of a material
 * slug, so a step fine enough for air is nearly twice as fine as material
 * needs. Sizing each run to its own contents is most of the flow's triangle
 * cost back, for nothing — most of the pipework on a compounding line carries
 * product, and product is the long one.
 */
export function stepFor(m: FlowMedium): number {
  if (m === 'none') return 0;
  const st = FLOW_STYLES[m];
  return ((st.head + st.tail) * st.wave) / SECTIONS_PER_SLUG;
}

/** The same two streams as flat ink, for the HUD legend. */
export const FLOW_INK: Record<'material' | 'air', string> = {
  material: '#f0a52e',
  air: '#5ac8f5',
};

/**
 * Where in its slug a point `phase` wavelengths along the run sits, 0..1.
 *
 * Smooth at both ends. A hard edge on a band travelling at sixty frames a
 * second strobes badly — it beats against the frame rate and reads as a row of
 * marks flickering rather than as anything moving — and that softness is the
 * whole difference between the two.
 */
export function slug(phase: number, head: number, tail: number): number {
  let f = phase - Math.floor(phase);
  if (f < head) {
    const x = f / head;
    return x * x * (3 - 2 * x);
  }
  f -= head;
  if (f < tail) {
    const x = 1 - f / tail;
    return x * x * (3 - 2 * x);
  }
  return 0;
}

/**
 * How strongly a slug is passing a point `s` world units along a run, 0..1.
 *
 * `travel` is elapsed animation seconds; each stream turns it into its own
 * distance here, so material and air stay in step however long the animation
 * has been on.
 *
 * Distance is always measured from the run's `from` end, so a run flowing the
 * way it was drawn SUBTRACTS it from the clock and a reversed one adds it.
 * Written this way round — clock first, distance second — the profile keeps its
 * sharp edge at the front and its long tail behind whichever way the run goes.
 * Mirroring the phase instead would quietly mirror the slug with it, and a
 * vacuum line would run with its tail first.
 */
export function slugAt(st: FlowStyle, s: number, travel: number, reversed: boolean): number {
  return slug((travel * st.speed + (reversed ? s : -s)) / st.wave, st.head, st.tail);
}
